/**
 * =============================================================================
 * TemplateLibraryTab — aba "Templates" da página Templates do Sistema
 *
 * Estrutura espelhada em `CustomerListsTab` (Central de Importação de Listas):
 * barra de ferramentas com busca, cartão com a tabela, menu de ações por linha
 * e paginador no rodapé.
 *
 * @version 1.0.0
 * RELEASE: 2026-09-08 v1.0.0 - Versão inicial (padronização com Importação de Listas).
 * =============================================================================
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Download,
  Layers,
  MoreHorizontal,
  RefreshCw,
  Search,
  Upload,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PaginationFooter } from '@/components/ui/pagination-footer';

import { useTemplatePackages } from '@/hooks/useSystemTemplates';
import type { TemplatePackageListItem } from '@/lib/template-system/api';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

interface TemplateLibraryTabProps {
  /** Abre a janela de geração de template. */
  onExport: () => void;
  /** Abre a janela de envio de arquivo. */
  onImport: () => void;
  /** Baixa o arquivo de uma versão. */
  onDownload: (versionId: string) => void;
  downloading?: boolean;
}

export function TemplateLibraryTab({
  onExport,
  onImport,
  onDownload,
  downloading,
}: TemplateLibraryTabProps) {
  const { data: pacotes = [], isLoading, isFetching, refetch } = useTemplatePackages();

  const [buscaInput, setBuscaInput] = useState('');
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(25);

  // Busca com espera curta para não filtrar a cada tecla
  useEffect(() => {
    const t = setTimeout(() => {
      setBusca(buscaInput.trim().toLowerCase());
      setPagina(1);
    }, 300);
    return () => clearTimeout(t);
  }, [buscaInput]);

  // Filtro e paginação acontecem em memória (a lista da conta é curta)
  const filtrados = useMemo<TemplatePackageListItem[]>(() => {
    if (!busca) return pacotes;
    return pacotes.filter(
      (p) =>
        p.name.toLowerCase().includes(busca) ||
        (p.segment ?? '').toLowerCase().includes(busca),
    );
  }, [pacotes, busca]);

  const visiveis = useMemo(
    () => filtrados.slice((pagina - 1) * tamanhoPagina, pagina * tamanhoPagina),
    [filtrados, pagina, tamanhoPagina],
  );

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2 p-3 border-b bg-muted/30">
        <div className="flex flex-wrap items-center gap-2 flex-1">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome..."
              value={buscaInput}
              onChange={(e) => setBuscaInput(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
          </div>
          {buscaInput && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setBuscaInput('')}
              className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
            >
              <X className="h-3 w-3 mr-1" />
              Limpar
            </Button>
          )}
          {isFetching && (
            <span className="text-xs text-muted-foreground animate-pulse">Atualizando...</span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="xs" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`mr-1.5 ${isFetching ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <Button variant="outline" size="xs" onClick={onImport}>
            <Upload className="mr-1.5" />
            Importar
          </Button>
          <Button size="xs" onClick={onExport}>
            <Download className="mr-1.5" />
            Gerar template
          </Button>
        </div>
      </div>

      <div className="p-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Layers className="h-5 w-5" />
              Templates da conta
            </CardTitle>
            <CardDescription>
              Pacotes de configuração gerados ou recebidos nesta conta
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : filtrados.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Layers className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">Nenhum template encontrado</p>
                <p className="text-sm">
                  {busca
                    ? 'Ajuste a busca ou limpe o filtro.'
                    : 'Clique em "Gerar template" para criar o primeiro.'}
                </p>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Segmento</TableHead>
                      <TableHead>Versão atual</TableHead>
                      <TableHead className="text-right">Versões</TableHead>
                      <TableHead>Atualizado em</TableHead>
                      <TableHead className="w-16 text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visiveis.map((pacote) => (
                      <TableRow key={pacote.id}>
                        <TableCell className="font-medium">
                          <span className="flex items-center gap-2">
                            {pacote.name}
                            {pacote.is_archived && (
                              <Badge variant="outline" className="text-[10px]">
                                Arquivado
                              </Badge>
                            )}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {pacote.segment ?? '—'}
                        </TableCell>
                        <TableCell className="text-sm">
                          {pacote.current_version_number
                            ? `v${pacote.current_version_number}`
                            : '—'}
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          {pacote.versions_count ?? 0}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(pacote.updated_at).toLocaleString('pt-BR')}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                                <span className="sr-only">Ações</span>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-52">
                              <DropdownMenuItem
                                disabled={!pacote.current_version_id || downloading}
                                onClick={() =>
                                  pacote.current_version_id &&
                                  onDownload(pacote.current_version_id)
                                }
                              >
                                <Download className="h-4 w-4 mr-2" />
                                Baixar arquivo
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <div className="border-t mt-2 px-4 py-2 bg-background">
                  <PaginationFooter
                    totalItems={filtrados.length}
                    currentPage={pagina}
                    pageSize={tamanhoPagina}
                    onPageChange={setPagina}
                    onPageSizeChange={(tamanho) => {
                      setTamanhoPagina(tamanho);
                      setPagina(1);
                    }}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    borderClassName=""
                    className="mt-0 pt-0"
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

export default TemplateLibraryTab;
